import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { withTranslation } from 'react-i18next';
import type { WithTranslation } from 'react-i18next';

/**
 * The last thing between a thrown render and a blank tab.
 *
 * React's contract for an uncaught error during render is to unmount the whole
 * tree, and without a boundary the whole tree is the application. The customer
 * gets a white page: no message, no navigation, no way back, and — because the
 * requests all succeeded — nothing in the network log that looks wrong either.
 * It reads as "the site is down" for what is usually one bad string in one
 * caption, which is how the feedback board disappeared for every customer who
 * had submitted an idea.
 *
 * So this sits directly around the routed page and nothing else. The chrome
 * outside it — the nav, the language switch, the help panel — keeps rendering,
 * which is the difference between a broken screen and a broken product: one
 * page failing leaves every other page one click away.
 *
 * Reset is keyed on the route rather than offered as a button. A boundary that
 * has caught is stuck until something tells it to try again, and "navigate
 * somewhere else" is both the thing people already do and the only signal here
 * that anything has actually changed. Retrying the same render that just threw
 * would only throw again.
 */
interface Props extends WithTranslation {
  /** Changing this clears a caught error — see the note above. */
  resetKey: string;
  children: ReactNode;
}

interface State {
  failed: boolean;
}

class PageErrorBoundaryBase extends Component<Props, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidUpdate(previous: Props) {
    if (previous.resetKey !== this.props.resetKey && this.state.failed) {
      this.setState({ failed: false });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // The console is the whole report. There is no client error sink in this
    // app, and inventing one here would mean deciding where customer data goes
    // — a decision that belongs somewhere other than a catch block.
    console.error('Page render failed', error, info.componentStack);
  }

  override render() {
    if (!this.state.failed) return this.props.children;

    const { t } = this.props;
    return (
      <div className="card" role="alert">
        <h1 className="text-lg font-semibold">{t('pageError.title')}</h1>
        <p className="mt-2 text-sm text-[color:var(--color-muted)]">{t('pageError.body')}</p>
        <p className="mt-4">
          {/* '/' rather than any named page, because HomeRedirect is what
              knows where this particular person can actually go — a company
              on Free, or a user without reports:read, has no dashboard, and
              sending the one recovery link on the screen to a second screen
              they cannot open is worse than not offering one.

              A full load rather than a router navigation: whatever state the
              page was holding when it threw is exactly what should not be
              carried into the next one. */}
          <a className="btn btn-secondary" href="/">
            {t('pageError.action')}
          </a>
        </p>
      </div>
    );
  }
}

export const PageErrorBoundary = withTranslation()(PageErrorBoundaryBase);
